import { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Action, Dispatch } from 'redux';

import lang from '../../lib/language';
import { PurchasePaymentTypeEnum } from '../../lib/models/crm/types';
import { RootState } from '../../lib/store';
import { uploadUserPreferencesData } from '../../lib/store/app';
import { getCurrentDetailedAccount, hasAnyAccountPaidPlan } from '../../lib/store/crm/helpers';
import MarketStateCache from '../../lib/store-util/MarketStateCache';
import { isMarketRegularHours } from '../../lib/util/MarketDataHelpers';

import IconClose from '../../assets/img/icon-close-dark-red.svg';
import { PaymentMethodSelectionState } from '../../containers/Enrollment/PaymentMethodSelection/types';
import { useIsMobile } from '../../hooks';
import BaseModal from '../../shared_components/BaseModal/BaseModal';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { UI_ROUTES } from '../../util/routes';
import { MobileDrawer } from '../index';

import styles from './SolanaUnsuccessfulPaymentModal.module.scss';

const SolanaUnsuccessfulPaymentModal = ({
  isSolanaPaymentRejected,
  hasPaidPlan,
  totalLossReached,
  rejectedSolanaPayments,
  shownSolanaRejectedPaymentId,
  userPreferencesData,
  uploadPreferences,
  individualId,
  accountDetail,
}: ReduxProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useIsMobile();
  // Only show for Plan/PlanWithAutoPass and DailyLossRewind — not for AutoPass
  const relevantRejectedPayment = rejectedSolanaPayments.find(
    (p) =>
      p.purchase_type_id === PurchasePaymentTypeEnum.Plan
      || p.purchase_type_id === PurchasePaymentTypeEnum.PlanWithAutoPass
      || p.purchase_type_id === PurchasePaymentTypeEnum.AccountDailyLossRewind,
  );
  const lastPaymentId = relevantRejectedPayment?.payment_id;
  const isDailylossRewindPayment = relevantRejectedPayment?.purchase_type_id
    === PurchasePaymentTypeEnum.AccountDailyLossRewind;
  const modalNotNeeded = !!hasPaidPlan && !totalLossReached && !isDailylossRewindPayment;

  const { marketState } = MarketStateCache.use();
  const isMarketHours = isMarketRegularHours(marketState);

  const alreadyDismissed = lastPaymentId !== undefined && shownSolanaRejectedPaymentId === lastPaymentId;
  const [open, setOpen] = useState<boolean>(isSolanaPaymentRejected && !alreadyDismissed);

  const isMainScreen = (
    isMobile
      ? [UI_ROUTES.dashboard, UI_ROUTES.overview, UI_ROUTES.discover]
      : [UI_ROUTES.dashboard, UI_ROUTES.overview, UI_ROUTES.discover, UI_ROUTES.tradeHistory]
  ).includes(location.pathname as UI_ROUTES);

  useEffect(() => {
    if (isSolanaPaymentRejected && lastPaymentId !== undefined) {
      setOpen(shownSolanaRejectedPaymentId !== lastPaymentId);
    }
  }, [isSolanaPaymentRejected, lastPaymentId, shownSolanaRejectedPaymentId]);

  const closeModal = () => {
    if (lastPaymentId !== undefined) {
      uploadPreferences({
        ...userPreferencesData,
        shownSolanaRejectedPaymentId: lastPaymentId,
      });
    }
    setOpen(false);
  };

  const continueHandler = () => {
    if (isDailylossRewindPayment) {
      sessionStorage.removeItem('paymentGatewayURL');
      if (!individualId || !accountDetail?.account_id) return;

      const historyStateParams: PaymentMethodSelectionState = {
        accountId: accountDetail.account_id,
        individualId,
        purchaseType: PurchasePaymentTypeEnum.AccountDailyLossRewind,
        prevPage: UI_ROUTES.products,
      };

      closeModal();
      navigate(
        {
          pathname: UI_ROUTES.paymentMethodSelection,
        },
        { state: historyStateParams },
      );
    } else {
      closeModal();
      navigate(UI_ROUTES.products);
    }
  };

  const renderContent = () => (
    <>
      <div className={styles.closeIconWrapper}>
        <img className={styles.closeIcon} src={IconClose} alt="Close" />
      </div>
      <div className={styles.title}>
        {isDailylossRewindPayment
          ? lang.cryptoPaymentUnsuccessfulPaymentTitle2()
          : lang.cryptoPaymentUnsuccessfulPaymentTitle()}
      </div>
      <div className={styles.text}>{lang.cryptoPaymentUnsuccessfulPaymentDescription()}</div>
      <div className={styles.buttonsWrapper}>
        <Button fullWidth biggerFontSize onClick={continueHandler}>
          {lang.commonTryAgain()}
        </Button>
        <Button
          fullWidth
          biggerFontSize
          variant={ButtonTypesEnum.Secondary}
          onClick={() => closeModal()}
        >
          {lang.commonNotNow()}
        </Button>
      </div>
    </>
  );

  if (!isMainScreen || modalNotNeeded) {
    return null;
  }

  const isCreatedToday = relevantRejectedPayment?.create_date
    ? relevantRejectedPayment.create_date.slice(0, 10) === new Date().toISOString().slice(0, 10)
    : false;

  if (isDailylossRewindPayment && (!isMarketHours || !isCreatedToday)) {
    return null;
  }

  if (isMobile) {
    return (
      <MobileDrawer isOpen={open} title="" onClose={closeModal}>
        {renderContent()}
      </MobileDrawer>
    );
  }

  return (
    <BaseModal isOpen={open} toggle={closeModal}>
      {renderContent()}
    </BaseModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  totalLossReached: state.closeout.accountStatusData.totalLossReached,
  isSolanaPaymentRejected: state.payment.isSolanaPaymentRejected,
  shownSolanaRejectedPaymentId: state.app.userPreferencesData?.shownSolanaRejectedPaymentId as
    | number
    | undefined,
  userPreferencesData: state.app.userPreferencesData,
  rejectedSolanaPayments: state.payment.rejectedSolanaPayments,
  individualId: state.crm.individualOrCompanyEmployeeId,
  accountDetail: getCurrentDetailedAccount(state),
  hasPaidPlan: hasAnyAccountPaidPlan(state.crm),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  uploadPreferences: (payload: any) => dispatch(uploadUserPreferencesData(payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(SolanaUnsuccessfulPaymentModal);
